import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';

const AddDependentForm = ({ onSubmit, onCancel, loading }) => {
  const [formData, setFormData] = useState({
    name: '',
    relationship: '',
    age: '',
    dateOfBirth: '',
    gender: '',
    bloodGroup: ''
  });
  const [errors, setErrors] = useState({});

  const relationshipOptions = [
    { value: '', label: 'Select relationship' },
    { value: 'child', label: 'Child' },
    { value: 'parent', label: 'Parent' },
    { value: 'spouse', label: 'Spouse' },
    { value: 'sibling', label: 'Sibling' },
    { value: 'caregiver', label: 'Caregiver' },
    { value: 'other', label: 'Other' }
  ];

  const genderOptions = [
    { value: '', label: 'Select gender' },
    { value: 'male', label: 'Male' },
    { value: 'female', label: 'Female' },
    { value: 'other', label: 'Other' },
    { value: 'prefer_not_to_say', label: 'Prefer not to say' }
  ];

  const bloodGroupOptions = [
    { value: '', label: 'Select blood group' },
    { value: 'a-positive', label: 'A+' },
    { value: 'a-negative', label: 'A-' },
    { value: 'b-positive', label: 'B+' },
    { value: 'b-negative', label: 'B-' },
    { value: 'ab-positive', label: 'AB+' },
    { value: 'ab-negative', label: 'AB-' },
    { value: 'o-positive', label: 'O+' },
    { value: 'o-negative', label: 'O-' }
  ];

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.name?.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData?.relationship) {
      newErrors.relationship = 'Relationship is required';
    }

    if (!formData?.age && !formData?.dateOfBirth) {
      newErrors.age = 'Either age or date of birth is required';
    }

    if (formData?.age && (isNaN(formData?.age) || formData?.age < 0 || formData?.age > 150)) {
      newErrors.age = 'Please enter a valid age';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleChange = (e) => {
    // Handle both event objects (from Input) and direct values (from Select)
    let fieldName, fieldValue;
    
    if (e?.target) {
      // Event object from Input component
      fieldName = e?.target?.name;
      fieldValue = e?.target?.value;
    } else {
      // Direct value from Select component - need to get name from context
      // This shouldn't happen with current implementation
      return;
    }
    
    setFormData(prev => ({ ...prev, [fieldName]: fieldValue }));
    if (errors?.[fieldName]) {
      setErrors(prev => ({ ...prev, [fieldName]: '' }));
    }
  };

  const handleSelectChange = (name) => (value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors?.[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <Input
          label="Full Name"
          name="name"
          value={formData?.name}
          onChange={handleChange}
          placeholder="Enter dependent's full name"
          error={errors?.name}
          required
        />
      </div>

      <div>
        <Select
          label="Relationship"
          name="relationship"
          value={formData?.relationship}
          onChange={handleSelectChange('relationship')}
          options={relationshipOptions}
          error={errors?.relationship}
          required
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <Input
            label="Age"
            name="age"
            type="number"
            value={formData?.age}
            onChange={handleChange}
            placeholder="Enter age"
            error={errors?.age}
          />
        </div>
        <div>
          <Input
            label="Date of Birth"
            name="dateOfBirth"
            type="date"
            value={formData?.dateOfBirth}
            onChange={handleChange}
          />
        </div>
      </div>

      <div>
        <Select
          label="Gender"
          name="gender"
          value={formData?.gender}
          onChange={handleSelectChange('gender')}
          options={genderOptions}
        />
      </div>

      <div>
        <Select
          label="Blood Group"
          name="bloodGroup"
          value={formData?.bloodGroup}
          onChange={handleSelectChange('bloodGroup')}
          options={bloodGroupOptions}
        />
      </div>

      <div className="flex gap-3 pt-4">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={loading}
          className="flex-1"
        >
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={loading}
          className="flex-1"
        >
          {loading ? (
            <>
              <Icon name="Loader2" size={18} className="animate-spin" strokeWidth={2} />
              Adding...
            </>
          ) : (
            'Add Dependent'
          )}
        </Button>
      </div>
    </form>
  );
};

export default AddDependentForm;